import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";

type NewsCardProps = {
  item: NewsItem;
  variant?: "row" | "compact" | "feature";
};

const categoryStyle: Record<string, string> = {
  行业动态: "bg-primary-100 text-primary-800",
  新片报道: "bg-accent-100 text-accent-900",
  明星访谈: "bg-secondary-100 text-secondary-900",
};

export default function NewsCard({ item, variant = "row" }: NewsCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/news/${item.id}`}
        className="group flex cursor-pointer gap-3 border-b border-background-200 py-3 last:border-b-0"
      >
        <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={item.image}
            alt={`${item.title} 配图`}
            title={`${item.title} - 影视资讯`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-600">
            {item.title}
          </h4>
          <p className="mt-1 font-label text-[11px] text-foreground-500">
            {item.date} · {item.category}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/news/${item.id}`}
        className="group block cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      >
        <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
          <img
            src={item.image}
            alt={`${item.title} 配图`}
            title={`${item.title} - 影视资讯`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${categoryStyle[item.category] ?? "bg-secondary-100 text-secondary-900"}`}>
              {item.category}
            </span>
            <span className="font-label text-[11px] text-foreground-500">{item.date}</span>
          </div>
          <h3 className="mt-2 line-clamp-2 text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
            {item.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-xs leading-6 text-foreground-600">{item.summary}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/news/${item.id}`}
      className="group grid cursor-pointer grid-cols-1 gap-4 border-b border-background-200 py-5 last:border-b-0 sm:grid-cols-[220px_1fr]"
    >
      <div className="aspect-[16/10] w-full overflow-hidden rounded-md bg-background-200 sm:h-[138px] sm:aspect-auto">
        <img
          src={item.image}
          alt={`${item.title} 配图`}
          title={`${item.title} - 影视资讯`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-col justify-center">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${categoryStyle[item.category] ?? "bg-secondary-100 text-secondary-900"}`}>
            {item.category}
          </span>
          <span className="font-label text-[11px] text-foreground-500">{item.date}</span>
          <span className="text-[11px] text-foreground-400">{item.author}</span>
        </div>
        <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{item.summary}</p>
        <span className="mt-3 inline-flex items-center gap-1 font-label text-xs text-primary-600">
          阅读全文
          <i className="ri-arrow-right-line text-sm" />
        </span>
      </div>
    </Link>
  );
}